import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getToken, getRole, api } from "../api/api";

export default function ProtectedRoute({ children, requiredRole }) {
  const { user, role, loading } = useAuth();
  const location = useLocation();
  const [validating, setValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  // High security: Validate token with backend before allowing access
  useEffect(() => {
    const validateAccess = async () => {
      const token = getToken();
      const storedRole = getRole();
      
      if (!token || !storedRole) {
        setIsValid(false);
        setValidating(false);
        return;
      }

      // Verify role matches required role
      if (requiredRole && storedRole !== requiredRole) {
        setIsValid(false);
        setValidating(false);
        return;
      }

      // If user and role are already set from AuthContext (e.g., after login),
      // trust it initially and validate in background
      if (user && role && role === storedRole) {
        setIsValid(true);
        setValidating(false);
        
        // Validate in background (non-blocking)
        try {
          let validationEndpoint;
          if (storedRole === "admin") {
            validationEndpoint = "/admin/profile";
          } else if (storedRole === "member") {
            validationEndpoint = "/member/profile";
          } else if (storedRole === "student") {
            validationEndpoint = "/student/user";
          } else {
            return;
          }
          
          await api(validationEndpoint);
          // Token is valid, keep isValid true
        } catch (error) {
          // Token invalid - but don't block if user is already logged in
          // The AuthContext will handle clearing auth on next validation
          console.warn("Token validation failed in background:", error);
        }
        return;
      }

      // No user in context - validate token before allowing access
      try {
        let validationEndpoint;
        if (storedRole === "admin") {
          validationEndpoint = "/admin/profile";
        } else if (storedRole === "member") {
          validationEndpoint = "/member/profile";
        } else if (storedRole === "student") {
          validationEndpoint = "/student/user";
        } else {
          setIsValid(false);
          setValidating(false);
          return;
        }
        
        await api(validationEndpoint);
        setIsValid(true);
      } catch (error) {
        // Token invalid or expired
        setIsValid(false);
      } finally {
        setValidating(false);
      }
    };

    if (!loading) {
      validateAccess();
    }
  }, [loading, requiredRole, user, role]);

  if (loading || validating) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" role="status" aria-label="Loading" />
      </div>
    );
  }

  // Check if user is logged in (from AuthContext) and has correct role
  const hasUserAndRole = user && role && role === requiredRole;
  const hasToken = getToken() && getRole() === requiredRole;
  
  // If user and role are set from login, trust it and allow access
  // This prevents redirect loop right after login when validation might fail temporarily
  if (hasUserAndRole && hasToken) {
    return children;
  }
  
  // No user or token - redirect to login
  if (!hasUserAndRole && !hasToken) {
    const loginPath = requiredRole === "admin" ? "/admin/login" : requiredRole === "member" ? "/member/login" : "/student/login";
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }
  
  // Fallback: if we have token but no user yet, wait a bit (shouldn't happen normally)
  if (hasToken && !hasUserAndRole) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (requiredRole && role !== requiredRole) {
    const home = role === "admin" ? "/admin" : role === "member" ? "/member" : "/student";
    return <Navigate to={home} replace />;
  }

  return children;
}
